import { createSelector } from '@app/utils/createSelector'

import { withAbortSignal } from '@app/packages/abortContext/actions'
import type { ChatServerApi } from '@app/packages/chat/Api'
import { dispose } from '@app/packages/disposable/disposable'
import { spawn } from '@app/packages/disposable/spawn'
import { resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { chatApiOptions, setChatApi, setChatApiPromise } from '@app/store/actions/contexts/chatApi'
import { getErrorReportController } from '@app/store/actions/contexts/errorReportController'
import { profileChatUidSelector } from '@app/store/selectors/profile'
import { chatConfigSelector } from '@app/store/slices/app_config'
import { configSelector } from '@app/store/slices/config'
import { hasGuestChatSessionSelector } from '@app/store/slices/guest_chat_session'
import { isAuthenticatedSelector, isSupervisedSelector } from '@app/store/slices/session'
import { type Store, waitForMappedValue } from '@app/store/store'

import { ForegroundTracker } from './ForegroundTracker'

/**
 * Boots chat in two phases: the REST facade goes up as soon as app_config
 * resolves (components need it for settings and attachments), then the manager
 * waits for a chat principal — an authenticated user or a guest who raised a
 * support session — before importing the chat bundle, so landing pages and
 * guests who never opened support still never fetch it. Disposing aborts a
 * pending wait or tears the started manager down.
 *
 * Everything runs inside `spawn` so a missing chat_server config is reported
 * rather than propagated: `run.client` awaits the boot stack and rethrows, so an
 * unhandled failure here would leave the whole app unhydrated instead of only
 * breaking chat. The `/m` route still fails loudly on its own — `getChatApi`
 * throws into the pane's error boundary when the facade was never registered.
 *
 * The manager is replaced whenever the chat identity changes, which is what makes a
 * guest's chat become the user's on login without a reload.
 *
 * Polygon is skipped whole: its api answers every label with a throw, and its
 * chat is barred from connecting anyway (`shouldConnect`), so booting only
 * reports errors nobody can act on (FRONTEND-91N).
 */
export class ChatLoader {
  private readonly disposable: Disposable

  constructor(store: Store) {
    const errorReporter = store.dispatch(getErrorReportController())
    // Published before the first await, so a pane that opens mid-boot waits for the facade
    // rather than finding nothing (FRONTEND-91Z).
    const apiChannel = Promise.withResolvers<ChatServerApi>()

    this.disposable = spawn(
      async stack => {
        if (configSelector(store.getState()).isPolygon) return
        store.dispatch(setChatApiPromise(apiChannel.promise))

        const ab = stack.adopt(new AbortController(), controller => controller.abort())
        const config = await store.dispatch(withAbortSignal(ab.signal, resolveThunkSelector(chatConfigSelector)))
        ab.signal.throwIfAborted()

        const { ChatServerApi } = await import('@app/packages/chat/Api')
        ab.signal.throwIfAborted()
        const api = new ChatServerApi(chatApiOptions(store, config))
        store.dispatch(setChatApi(api))
        apiChannel.resolve(api)

        // The bundle stays behind this first wait: a landing page or a guest who never
        // opens support must not fetch it.
        await waitForMappedValue(store, chatPrincipalSelector, present => (present ? { val: true } : null), ab.signal)
        const { createChatManager } = await import('@app/store/actions/contexts/chatManager')
        ab.signal.throwIfAborted()

        const foregroundTracker = stack.use(new ForegroundTracker())
        let started: Disposable | null = null
        stack.defer(() => {
          if (started) dispose(started)
        })

        // A manager is bound to one identity: the socket joins `user:<chat_uid>` at
        // connect and the initial channel load runs once, both with the uid of the
        // moment. So a guest who signs in — or an account switch — needs a new manager,
        // not the in-band `auth:refresh` a rotated token gets.
        while (!ab.signal.aborted) {
          await waitForMappedValue(store, chatPrincipalSelector, present => (present ? { val: true } : null), ab.signal)
          const identity = chatIdentity(store)
          started = createChatManager(store, config, foregroundTracker.isForeground).start()

          await waitForMappedValue(store, chatIdentitySelector, next => (next === identity ? null : { val: true }), ab.signal)
          dispose(started)
          started = null
        }
      },
      e => {
        // Whoever is waiting on the facade has to learn it is never coming, or they hang.
        apiChannel.reject(e)
        // Tearing the loader down aborts the pending waits; that is not a failure.
        errorReporter.report(e)
      }
    )
  }

  [Symbol.dispose]() {
    dispose(this.disposable)
  }
}

/**
 * Someone the chat can act as: an authenticated user, or a guest who already
 * raised a support session. Landing pages and guests who never opened support
 * still never fetch the chat bundle.
 */
/**
 * Who the chat would connect as — and nobody, while supervising. Supervision reads as
 * authenticated, so the manager used to start and join `user:<chat_uid>` with the impersonated
 * user's uid: a supervisor sitting in that person's support conversation, with the server refusing
 * every send. That refusal is the disabled composer `SupportChatWidget` used to draw. Excluded
 * here rather than only in the widget, so the bundle is never fetched and no socket is opened.
 */
const chatPrincipalSelector = createSelector(
  [isAuthenticatedSelector, hasGuestChatSessionSelector, isSupervisedSelector],
  (authenticated, guest, supervised) => !supervised && (authenticated || guest)
)

/** Who the chat acts as. `undefined` and `null` both mean "nobody yet", so they compare equal. */
const chatIdentitySelector = createSelector([profileChatUidSelector], uid => uid ?? null)

const chatIdentity = (store: Store) => chatIdentitySelector(store.getState())
